import { BotAdaptiveCardDialogInfo, BotUrlDialogInfo, DialogDimension, DialogInfo, dialog } from "@microsoft/teams-js";
import { getSurveyTypeDisplay } from "@teams/cards";
import { getMeetingSurveyTakingTaskInfo } from "@teams/client-shared";
import { getAuthoringTaskInfo } from "@teams/client-shared";
import { TeamsSurvey } from "@teams/types";
import { SurveyDTO } from "@teams/types/api";

import { getAppId, getPageContext } from "./teamsHelpers";

export const startTask = (
  taskInfo: DialogInfo,
  onTaskCompleted?: (err: string | undefined, result: string | object | undefined ) => void
) => {
  const { url, card,height,width, title, fallbackUrl, completionBotId } = taskInfo;

  if (url){
    const dialogConfig: BotUrlDialogInfo = {
      url,
      fallbackUrl,
      title: title || "Dialog",
      size: { width:width || DialogDimension.Large,height: height|| DialogDimension.Large },
      completionBotId: completionBotId||getAppId()
    };
    dialog.url.bot.open(dialogConfig, ({err, result}) => {
      if (err && err !== "CancelledByUser") {
        console.error("Error: startTask.onTaskCompleted", err, result);
      }
      if (onTaskCompleted) {
        onTaskCompleted(err , result);
      }
    });
  } else if (card){
    const dialogConfig:BotAdaptiveCardDialogInfo = {
      card,
      title: title || "Dialog",
      size: { width:width || DialogDimension.Large,height: height|| DialogDimension.Large },
      completionBotId: completionBotId||getAppId()
    };
    dialog.adaptiveCard.bot.open(dialogConfig, ({err, result}) => {
      if (err && err !== "CancelledByUser") {
        console.error("Error: startTask.onTaskCompleted", err, result);
      }
      if (onTaskCompleted) {
        onTaskCompleted(err , result);
      }
    });
  }

};

export const startTaskAsync = (taskInfo: DialogInfo): Promise<string | object |  undefined> => {
  return new Promise(resolve => {
    startTask(taskInfo, (err, result) => {
      resolve(err || result);
    });
  });
};

export const startAuthoringTask = (
  onTaskCompleted?: (err: string | undefined, result: string | object | undefined) => void,
  customRecipient?: boolean
) => {
  const context = getPageContext();
  let url = `${window.location.origin}${context}`;

  if (context === "/agile") {
    url += "/content/agile-standup/authoring";
  } else {
    const queryParams = new URLSearchParams();
    if (customRecipient) {
      queryParams.set("customRecipient", true.toString());
      queryParams.set("isPersonal", true.toString());
    }

    url += `/content/start?${queryParams.toString()}`;
  }

  return startTask(
    {
      url,
      height: DialogDimension.Large,
      width: 700,
      title: "Authoring",
      completionBotId: getAppId(),
    },
    onTaskCompleted
  );
};

export const startNpsSurveyTask = (
  onTaskCompleted?: (err: string | undefined, result: string | object | undefined) => void
) => {
  let url = `${window.location.origin}/msteams/content/nps`;
  return startTask(
    {
      url,
      height: 350,
      width: 701,
      title: "",
      completionBotId: getAppId(),
    },
    onTaskCompleted
  );
};

export const getCheckOutTaskInfo = (
  isTrial: boolean,
  trackingSource: string,
  screenWidth?: number
): Pick<DialogInfo, "url" | "width" | "height"> => {
  const url = `/msteams/content/checkout/native?trial=${isTrial}&trackingSource=${trackingSource}`;
  let width: number | DialogDimension;
  let height: number | DialogDimension;

  if (!screenWidth) {
    width = DialogDimension.Large;
    height = DialogDimension.Large;
  } else if (screenWidth > 1300) {
    width = 900;
    height = 800;
  } else {
    width = DialogDimension.Large;
    height = 2500;
  }

  return {
    url: url,
    height,
    width,
  };
};

export const startCheckoutTask = (
  isTrial: boolean,
  trackingSource: string,
  screenWidth?: number,
  onTaskCompleted?: (err: string | undefined, result: string | object | undefined) => void
) => {
  const checkOutTaskInfo = getCheckOutTaskInfo(isTrial, trackingSource, screenWidth);
  return startTask(
    {
      url: `${window.location.origin}${checkOutTaskInfo.url}`,
      width: checkOutTaskInfo.width,
      height: checkOutTaskInfo.height,
      title: "Checkout",
      completionBotId: getAppId(),
    },
    onTaskCompleted
  );
};

export function startPollyEditTask(
  survey: SurveyDTO,
  onTaskCompleted: dialog.DialogSubmitHandler
) {
  const taskInfo = getAuthoringTaskInfo(window.location.origin, survey.type, "msteams", {
    surveyId: survey.id,
    title: "Editing polly",
  });

  dialog.url.bot.open(
    {
      ...taskInfo,
      completionBotId: getAppId(),
    },
    onTaskCompleted
  );
}

export function startPollyReopenTask(
  survey: SurveyDTO,
  onTaskCompleted?: (err: string | undefined, result: string | object | undefined) => void
) {
  const taskInfo = getAuthoringTaskInfo(window.location.origin, survey.type, "msteams", {
    surveyId: survey.id,
    title: "Reopen polly",
    reOpen: true,
  });

  startTask(
    {
      url: taskInfo.url,
      title: taskInfo.title,
      size:{height: 240, width: 550},
      completionBotId: getAppId(),
    } as DialogInfo,
    onTaskCompleted
  );
}

export function startPollyDuplicateTask(
  survey: SurveyDTO,
  onTaskCompleted?: dialog.DialogSubmitHandler
) {
  const taskInfo = getAuthoringTaskInfo(window.location.origin, survey.type, "msteams", {
    surveyId: survey.id,
    duplicate: true,
    title: "Duplicate",
  });

  dialog.url.bot.open(
    {
      ...taskInfo,
      completionBotId: getAppId(),
    },
    onTaskCompleted
  );
}

export async function startMeetingSurveyTakingTask(survey: SurveyDTO, allowRetake?: boolean) {
  const settings = getMeetingSurveyTakingTaskInfo(
    window.location.origin,
    survey as TeamsSurvey,
    allowRetake
  );
  await startTaskAsync({
    title: getSurveyTypeDisplay(survey.type, { capitalize: true }),
    url: settings.url,
    height: settings.height as number,
    width: settings.width as number,
    completionBotId: getAppId(),
  });
}
